import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

// Define the shape of our authentication context
interface AuthContextType {
  user: any | null;  // Store user data (ID, email)
  isAuthenticated: boolean;
  loading: boolean;  // ✅ Add loading state
  handleLogout: () => Promise<void>;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to access the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// AuthProvider component that wraps the app and provides authentication state
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true); // ✅ Track loading state

  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getUser();
      console.log("Supabase session:", data.user); // Debugging log
      if (data?.user) {
        setUser(data.user);
      }
      setLoading(false); // ✅ Ensure we stop loading
    };

    checkSession();

    // Listen for authentication state changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false); // ✅ Stop loading when state changes
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error logging out:", error);
    } else {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, loading, handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
};
